// common/features/profile/family/constants.ts
//
// Family relationship vocabulary (data only). Shared between mobile and web —
// keep free of platform-specific imports (no react-native, no DOM APIs).
// Relationship helper functions live in ./utils.

// Mirrors the live /types/relationship vocabulary (names as the BE returns
// them). "Me" is the player themselves; it stays here for coercing/recognizing
// backend values but is excluded from the selectable form options below.
export const RELATIONSHIP_OPTIONS = [
  'Mother',
  'Father',
  'Guardian',
  'Grand Mother',
  'Grand Father',
  'Me',
  'Other',
] as const

export type RelationshipOption = (typeof RELATIONSHIP_OPTIONS)[number]

// Options shown in the contact form's relationship picker. "Me" is the player
// themselves and is never a valid choice for a family contact, so it is excluded
// from the selectable list (it stays in RELATIONSHIP_OPTIONS for coercing/
// recognizing backend values).
export const RELATIONSHIP_FORM_OPTIONS = RELATIONSHIP_OPTIONS.filter(
  (opt) => opt !== 'Me'
) as readonly RelationshipOption[]

// BE RelationshipType ids that classify a contact as a parent/guardian:
// 1 Mother, 2 Father, 3 Guardian, 4 Grandparent. Kept as a fallback only —
// the live BE ids drift (e.g. "Grand Father" comes back as id 7, not 4), so
// grouping matches by NAME first (see PARENT_GUARDIAN_TYPE_NAMES) and only
// falls back to this id set. Everything else (Other, Self, missing) goes under
// "Additional Contacts".
export const PARENT_GUARDIAN_TYPE_IDS: ReadonlySet<number> = new Set([
  1, 2, 3, 4,
])

// Relationship NAMES that classify a contact as a parent/guardian, normalized
// to lowercase with spaces stripped so "Grand Father", "Grandfather", and
// "Grandparent" all match. This is the primary grouping signal because the BE
// relationship-type ids are not stable across environments. Any name not listed
// here (Other, etc.) and any missing relationship falls to Additional Contacts.
export const PARENT_GUARDIAN_TYPE_NAMES: ReadonlySet<string> = new Set([
  'mother',
  'father',
  'guardian',
  'grandparent',
  'grandmother',
  'grandfather',
])

// BE RelationshipType id for the player themselves. The Self contact is not a
// real family member — it's hidden from the Family Management list (but still
// selectable in the contact form's relationship dropdown).
export const SELF_RELATIONSHIP_TYPE_ID = 6

// BE `RelationshipType` enum (GET /types/relationship → {id, name}). Mirrors the
// live vocabulary — note ids are NOT sequential by category (Grand Mother is 4,
// Grand Father is 7). Used only as a fallback for label/id resolution; the
// canonical source is the /types/relationship endpoint (useRelationshipTypes).
export const RELATIONSHIP_TYPE_BY_ID: Record<number, string> = {
  1: 'Mother',
  2: 'Father',
  3: 'Guardian',
  4: 'Grand Mother',
  5: 'Other',
  6: 'Me',
  7: 'Grand Father',
}

// BE invitation `type` for the family (`user_to_player`) invitation — used to
// detect it on GET /invitations/:id. Matched by either id or name.
export const FAMILY_INVITE_TYPE_ID = '13'
export const FAMILY_INVITE_TYPE_NAME = 'user_to_player'

// Success confirmation copy for the family flows. Single source of truth shared
// by mobile (SuccessBottomSheet title/subtitle) and web (Ant toast rendered as
// "<title> — <subtitle>"). Delete has no success message on either platform.
export const FAMILY_SUCCESS_MESSAGES = {
  invite: {
    title: 'Invitation Sent',
    subtitle: 'We’ve emailed your invite, they will receive it shortly.',
  },
  edit: {
    title: 'Changes Saved',
    subtitle: 'The family member’s details have been updated.',
  },
  resend: {
    title: 'Invitation Resent',
    subtitle: 'We’ve emailed the invite again, they will receive it shortly.',
  },
} as const

// Support docs link surfaced from the Family Management UI (web + mobile) — the
// header help icon, the empty-state "click here", and the invite form's
// admin-access hint all point here.
export const FAMILY_SUPPORT_URL = 'https://support.sportninja.com/family'

// Label shown for the Self ("Me") relationship in the EDIT family-member picker
// only. The BE names this entry "Me"; in the edit flow we relabel it to make the
// action explicit (picking it marks the contact as the player themselves).
export const SELF_RELATIONSHIP_FORM_LABEL = '* Mark As Me'
